import { alphavantageConfig } from '../config/alphavantage';

export interface AlphavantageMonthlyData {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  adjustedClose: number;
  volume: number;
  dividend: number;
}

export interface TickerSentiment {
  ticker: string;
  relevance_score: string;
  ticker_sentiment_score: string; // Change to string since API returns string
  ticker_sentiment_label: string;
}

export interface Topic {
  topic: string;
  relevance_score: string;
}

export interface NewsItem {
  title: string;
  url: string;
  time_published: string;
  authors: string[];
  summary: string;
  banner_image: string;
  source: string;
  overall_sentiment_score: number;
  overall_sentiment_label: string;
  ticker_sentiment: TickerSentiment[];
  topics: Topic[];
}

export interface NewsSentimentResponse {
  items: string;
  sentiment_score_definition: string;
  relevance_score_definition: string;
  feed: NewsItem[];
}

export interface SearchResult {
  symbol: string;
  name: string;
  type: string;
  region: string;
  marketOpen: string;
  marketClose: string;
  timezone: string;
  currency: string;
  matchScore: string;
}

export interface SymbolSearchResponse {
  bestMatches: {
    "1. symbol": string;
    "2. name": string;
    "3. type": string;
    "4. region": string;
    "5. marketOpen": string;
    "6. marketClose": string;
    "7. timezone": string;
    "8. currency": string;
    "9. matchScore": string;
  }[];
}

export interface DividendData {
  ex_dividend_date: string;
  declaration_date: string;
  record_date: string;
  payment_date: string;
  amount: string;
}

export interface DividendResponse {
  symbol: string;
  data: DividendData[];
}

export class AlphavantageDataService {
  async getMonthlyAdjusted(symbol: string): Promise<AlphavantageMonthlyData[]> {
    const params = new URLSearchParams({
      function: 'TIME_SERIES_MONTHLY_ADJUSTED',
      symbol: symbol,
      apikey: alphavantageConfig.apiKey
    });

    const response = await fetch(`${alphavantageConfig.baseUrl}?${params}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch market data');
    }

    const data = await response.json();
    
    if (data['Error Message']) {
      throw new Error(data['Error Message']);
    }

    const monthlyData = data['Monthly Adjusted Time Series'];
    
    return Object.entries(monthlyData).map(([timestamp, values]: [string, any]) => ({
      timestamp,
      open: parseFloat(values['1. open']),
      high: parseFloat(values['2. high']),
      low: parseFloat(values['3. low']),
      close: parseFloat(values['4. close']),
      adjustedClose: parseFloat(values['5. adjusted close']),
      volume: parseInt(values['6. volume']),
      dividend: parseFloat(values['7. dividend amount'])
    })).reverse();
  }

  async getNewsSentiment(symbol: string): Promise<NewsSentimentResponse> {
    const params = new URLSearchParams({
      function: 'NEWS_SENTIMENT',
      tickers: symbol,
      sort: 'RELEVANCE',
      limit: '10',
      apikey: alphavantageConfig.apiKey
    });

    const response = await fetch(`${alphavantageConfig.baseUrl}?${params}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch news sentiment');
    }

    const data = await response.json();
    
    if (data['Error Message']) {
      throw new Error(data['Error Message']);
    }

    return data;
  }

  async searchSymbol(keywords: string): Promise<SearchResult[]> {
    const params = new URLSearchParams({
      function: 'SYMBOL_SEARCH',
      keywords,
      apikey: alphavantageConfig.apiKey
    });

    const response = await fetch(`${alphavantageConfig.baseUrl}?${params}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch search results');
    }

    const data: SymbolSearchResponse = await response.json();
    
    if (data['Error Message']) {
      throw new Error(data['Error Message']);
    }

    return (data.bestMatches || []).map(match => ({
      symbol: match['1. symbol'],
      name: match['2. name'],
      type: match['3. type'],
      region: match['4. region'],
      marketOpen: match['5. marketOpen'],
      marketClose: match['6. marketClose'],
      timezone: match['7. timezone'],
      currency: match['8. currency'],
      matchScore: match['9. matchScore']
    }));
  }

  async getDividends(symbol: string): Promise<DividendResponse> {
    const params = new URLSearchParams({
      function: 'DIVIDENDS',
      symbol,
      apikey: alphavantageConfig.apiKey
    });

    const response = await fetch(`${alphavantageConfig.baseUrl}?${params}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch dividend data');
    }

    const data = await response.json();
    
    if (data['Error Message']) {
      throw new Error(data['Error Message']);
    }

    return data;
  }
}
